import type { GridOptions } from 'ag-grid-community/dist/types/src/entities/gridOptions'
import { useTranslation } from 'react-i18next'
import { useGetCalendarSettings } from '@/api/endpoints/index.ts'
import type { ModifyCalendarSettingsJSON } from '@/api/model/modifyCalendarSettingsJSON'
import { dateFormatter, dateGetter } from '@/common/utils'
import { calcRemaining } from './vacationBalanceUtils.js'

const cellEditor = 'agNumberCellEditor'
const cellEditorParams = {
	precision: 0,
}

export const useGetVacationBalancesColumnDefs = (): GridOptions['columnDefs'] => {
	const { t } = useTranslation()
	const { data: calendarSettings } = useGetCalendarSettings<ModifyCalendarSettingsJSON>()
	const saturdaysCalculatedAsHoliday = calendarSettings?.saturdaysCalculatedAsHoliday !== false

	return [
		{
			headerName: t('Number', 'Numero'),
			field: 'personId',
			editable: false,
			filter: true,
		},
		{
			headerName: t('Name', 'Nimi'),
			field: 'name',
			editable: false,
		},
		{
			headerName: t('EmploymentStartDate', 'Työsuhteen alkupäivä'),
			field: 'employmentStartDate',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			editable: false,
		},
		{
			headerName: t('EarnedVacationDays', 'Ansaitut lomapäivät'),
			field: 'vacationAllowanceDays',
			cellEditor,
			cellEditorParams,
		},
		{
			headerName: t('ExtraDays', 'Lisäpäivät'),
			field: 'vacationExtraDays',
			cellEditor,
			cellEditorParams,
		},
		{
			headerName: t('UsedVacationDays', 'Pidetyt lomapäivät'),
			field: 'usedVacationDays',
			cellEditor,
			cellEditorParams,
		},
		{
			headerName: t('RemainingVacationDays', 'Lomapäiviä jäljellä'),
			field: 'remainingVacationDays',
			valueGetter: (params) => {
				// usedVacationDays from backend = businessDays + saturdaysUsed (CalendarDurationsUtils)
				// remaining = allowance - businessDaysUsed = allowance - (used - saturdaysUsed)
				const allowance = params.data?.vacationAllowanceDays ?? 0
				const extra = params.data?.vacationExtraDays ?? 0
				const used = params.data?.usedVacationDays ?? 0
				const satUsed = params.data?.saturdaysUsed ?? 0
				return calcRemaining(allowance, extra, used, satUsed, saturdaysCalculatedAsHoliday)
			},
			editable: false,
			suppressNavigable: true, // prevent cell from being focused when navigating with keyboard since it's a calculated field
			cellStyle: { backgroundColor: '#f0f8ff' },
		},
		...(saturdaysCalculatedAsHoliday
			? [
					{
						headerName: t('Saturdays', 'Lauantait'),
						field: 'saturdaysTotal',
						cellEditor,
						cellEditorParams,
					},
					{
						headerName: t('UsedSaturdays', 'Käytetyt lauantait'),
						field: 'saturdaysUsed',
						cellEditor,
						cellEditorParams,
					},
				]
			: []),
		/*
    {
      headerName: t("Status", "Tila"),
      field: "vacationYearStatus",
      editable: false,
    },
*/
	]
}
